import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { ingestorsApi, type IngestorEventRow } from './ingestors/api';
import { ConfigForm } from './ingestors/ConfigForm';
import { SecretForm } from './ingestors/SecretForm';
import { GrantXpPanel } from './ingestors/GrantXpPanel';
import type { IngestorInstance, IngestorType } from './ingestors/types';

interface Team {
  id: string;
  name: string;
}

export function IngestorDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const inst = useQuery({
    queryKey: ['ingestor-instance', id],
    queryFn: () => ingestorsApi.getInstance(id),
    enabled: !!id,
  });
  const types = useQuery({
    queryKey: ['ingestor-types'],
    queryFn: () => ingestorsApi.listTypes(),
  });
  const events = useQuery({
    queryKey: ['ingestor-instance-events', id],
    queryFn: () => ingestorsApi.recentEvents(id, 50),
    enabled: !!id,
    refetchInterval: 10_000,
  });
  // v0.6.2: stable local. Same pattern as the
  // PlayerDashboardPage fix — the api returns
  // `{events: [...]}` but the runtime can resolve to a
  // partial response. `events.data?.events.length` was
  // unsafe; the ?? [] makes the length check safe.
  const eventsList = events.data?.events ?? [];
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });

  const update = useMutation({
    mutationFn: (body: { name?: string; config?: Record<string, unknown>; secret?: Record<string, string>; enabled?: boolean }) =>
      ingestorsApi.updateInstance(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingestor-instance', id] }),
  });
  const remove = useMutation({
    mutationFn: () => ingestorsApi.deleteInstance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingestor-instances'] });
      navigate('/admin/ingestors');
    },
  });
  const test = useMutation({
    mutationFn: () => ingestorsApi.testConnection(id),
  });

  const [name, setName] = useState<string | null>(null);
  const [config, setConfig] = useState<Record<string, unknown> | null>(null);
  const [secret, setSecret] = useState<Record<string, string>>({});
  const [secretDirty, setSecretDirty] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (inst.isLoading) return <p className="text-ink-muted">Loading…</p>;
  if (inst.isError || !inst.data) return <p className="text-accent-danger">Failed to load ingestor.</p>;
  const i: IngestorInstance = inst.data.instance;
  const typeMeta: IngestorType | undefined = (types.data?.types ?? []).find((t) => t.type === i.type);
  const team = (teams.data?.teams ?? []).find((t) => t.id === i.team_id);

  const workingName = name ?? i.name;
  const workingConfig = config ?? i.config;
  const webhookUrl = `${window.location.origin}/api/ingest/${i.id}`;

  const save = async () => {
    setMsg(null);
    const body: Parameters<typeof ingestorsApi.updateInstance>[1] = {};
    if (name !== null && name !== i.name) body.name = name;
    if (config !== null && JSON.stringify(config) !== JSON.stringify(i.config)) body.config = config;
    if (secretDirty && Object.values(secret).some((v) => v.length > 0)) body.secret = secret;
    if (Object.keys(body).length === 0) {
      setMsg({ kind: 'ok', text: 'Nothing to update.' });
      return;
    }
    try {
      await update.mutateAsync(body);
      setName(null);
      setConfig(null);
      setSecret({});
      setSecretDirty(false);
      setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : 'Save failed.' });
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <Link to="/admin/ingestors" className="text-sm text-ink-muted hover:text-ink">← Ingestors</Link>
          <h1 className="mt-1 text-2xl font-semibold">{i.name}</h1>
          <p className="text-ink-muted">
            {typeMeta?.display_name ?? i.type} · {team?.name ?? i.team_id} ·{' '}
            <span className={i.enabled ? 'text-accent-success' : 'text-ink-dim'}>
              {i.enabled ? 'enabled' : 'disabled'}
            </span>
          </p>
          {i.poller_state ? <PollerHealth state={i.poller_state} /> : null}
        </div>
        <div className="flex gap-2">
          <button
            className="btn"
            onClick={async () => {
              setTestResult(null);
              try {
                const r = await test.mutateAsync();
                setTestResult(r.ok ? 'Test OK.' : `Test failed: ${r.error ?? 'unknown'}`);
              } catch (e) {
                setTestResult(e instanceof ApiError ? e.message : 'Test failed.');
              }
            }}
            disabled={test.isPending}
          >
            {test.isPending ? 'Testing…' : 'Test connection'}
          </button>
          {i.type === 'jira' ? (
            <Link to={`/admin/ingestors/${i.id}/transitions`} className="btn">Transitions</Link>
          ) : null}
          <Link to={`/admin/ingestors/${i.id}/actors`} className="btn">Actors</Link>
        </div>
      </header>

      {testResult ? (
        <div className="card text-sm" role="status">{testResult}</div>
      ) : null}
      {msg ? (
        <div
          className={msg.kind === 'ok' ? 'card text-sm border-l-4 border-l-accent-success' : 'card text-sm border-l-4 border-l-accent-danger'}
          role="status"
        >
          {msg.text}
        </div>
      ) : null}

      {i.type !== 'manual' && (typeMeta?.mode === 'webhook' || typeMeta?.mode === 'both') ? (
        <div className="card">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Webhook URL</h2>
          <p className="text-xs text-ink-dim">
            Configure this on the source side. The api verifies the signature using the secret below.
          </p>
          <pre className="mt-2 overflow-x-auto rounded-md bg-bg-elevated p-2 font-mono text-xs">{webhookUrl}</pre>
        </div>
      ) : null}

      {typeMeta?.mode === 'polling' || typeMeta?.mode === 'both' ? (
        <div className="card">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Polling</h2>
          <p className="text-xs text-ink-dim">
            This ingestor polls the source on a schedule. There is no webhook to configure on the
            source side — the api authenticates with the credentials below and queries the
            source for updates each cycle.
          </p>
          {i.poller_state ? (
            <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
              <dt className="text-ink-muted">Last poll completed</dt>
              <dd className="font-mono">{i.poller_state.last_poll_at ?? '—'}</dd>
              <dt className="text-ink-muted">Last poll started</dt>
              <dd className="font-mono">{i.poller_state.last_run_at ?? '—'}</dd>
              <dt className="text-ink-muted">Last error</dt>
              <dd className="font-mono text-accent-danger">{i.poller_state.last_error ?? '—'}</dd>
            </dl>
          ) : (
            <p className="mt-2 text-xs text-ink-muted">No poller state yet — first cycle pending.</p>
          )}
        </div>
      ) : null}

      {i.type === 'manual' ? <GrantXpPanel instanceId={i.id} teamId={i.team_id} /> : null}

      <div className="grid gap-6 md:grid-cols-2">
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Settings</h2>
          <div className="space-y-1">
            <label className="label" htmlFor="name">Name</label>
            <input
              id="name"
              className="input"
              value={workingName}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </div>
          <div className="space-y-1">
            <label className="label">Config</label>
            <ConfigForm
              type={i.type}
              value={workingConfig}
              onChange={(c) => setConfig(c)}
            />
          </div>
        </section>

        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Secrets</h2>
          <SecretForm
            type={i.type}
            value={secret}
            onChange={(s) => { setSecret(s); setSecretDirty(true); }}
            required={false}
          />
        </section>
      </div>

      <div className="flex items-center justify-between">
        <button className="btn-primary" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {!confirmDelete ? (
          <button className="btn-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-muted">Are you sure? Events already scored will not be re-scored.</span>
            <button
              className="btn-danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Yes, delete'}
            </button>
            <button className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
          </div>
        )}
      </div>

      <div className="card overflow-x-auto">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Recent events</h2>
          <p className="text-xs text-ink-dim">Auto-refreshes every 10 s. <code>Raw</code> = unredacted vendor payload; the TTL job drops it after INGESTOR_RAW_TTL_HOURS (default 24).</p>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Type</th>
              <th>External id</th>
              <th>Actor</th>
              <th>Awards</th>
              <th>Received</th>
              <th>Scored</th>
              <th>Raw</th>
            </tr>
          </thead>
          <tbody>
            {eventsList.map((e: IngestorEventRow) => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="py-2 font-mono text-xs">{e.type}</td>
                <td className="font-mono text-xs text-ink-muted">{e.external_id}</td>
                <td className="font-mono text-xs text-ink-muted">{e.actor_external ?? '—'}</td>
                <td>
                  {e.awards && e.awards.length > 0 ? (
                    <ul className="space-y-0.5">
                      {e.awards.map((a, i) => (
                        <li key={i} className="text-xs">
                          <span className="font-semibold text-accent">+{a.xp} XP</span>
                          {' > '}
                          <span className="font-mono text-ink-muted">{a.competency_id}</span>
                          {a.source === 'quest' ? <span className="text-ink-dim"> (quest)</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-xs text-ink-dim">—</span>
                  )}
                </td>
                <td className="text-ink-muted">{new Date(e.received_at).toLocaleString()}</td>
                <td>
                  {e.scored_at ? (
                    <span className="text-xs text-accent-success">scored</span>
                  ) : (
                    <span className="text-xs text-ink-dim">—</span>
                  )}
                </td>
                <td>
                  <RawStatusCell event={e} />
                </td>
              </tr>
            ))}
            {!eventsList.length ? (
              <tr>
                <td colSpan={7} className="py-4 text-center text-ink-muted">
                  {i.type === 'manual'
                    ? 'No grants yet. Use the form above to grant XP to a team member.'
                    : typeMeta?.mode === 'polling' || typeMeta?.mode === 'both'
                      ? <>No events yet. The poller will fetch updates on the next cycle{typeMeta?.mode === 'both' ? ', or send a webhook to the URL above' : ''}.</>
                      : <>No events received yet. Send a test webhook to <code className="font-mono">{webhookUrl}</code>.</>}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// PollerHealth: M5.7 follow-up. At-a-glance "is the poller
// working" widget for the ingestor detail page. Shows the last
// successful poll time as a relative duration ("2 min ago") with
// the absolute ISO timestamp in a title attribute for hover/precise
// lookups. The error line is only shown when last_error is set, and
// is the primary signal that something needs admin attention.
function PollerHealth({ state }: { state: { last_poll_at: string | null; last_run_at: string | null; last_error: string | null } }) {
  // Compute "X min ago" for the last successful poll. We use the
  // browser's current time (not the server's) so the relative
  // number matches what the admin sees on the wall clock.
  const lastPoll = state.last_poll_at ? new Date(state.last_poll_at) : null;
  const minutesAgo = lastPoll ? Math.floor((Date.now() - lastPoll.getTime()) / 60_000) : null;
  const stale = minutesAgo !== null && minutesAgo > 10; // > 10 min = warning
  return (
    <div className="mt-2 flex items-center gap-3 text-xs">
      <span
        className={state.last_error ? 'text-accent-danger' : stale ? 'text-accent-warning' : 'text-accent-success'}
        title={state.last_poll_at ? `Last polled: ${state.last_poll_at}` : 'Never polled'}
      >
        <span className="mr-1">{state.last_error ? '⚠' : stale ? '⏱' : '●'}</span>
        {state.last_error
          ? `Poller error: ${state.last_error.slice(0, 60)}${state.last_error.length > 60 ? '…' : ''}`
          : lastPoll
            ? `Polled ${minutesAgo === 0 ? 'just now' : `${minutesAgo} min ago`}`
            : 'Poller not yet started'}
      </span>
      {state.last_run_at ? (
        <span className="text-ink-dim" title={`Last started: ${state.last_run_at}`}>
          (started {new Date(state.last_run_at).toLocaleTimeString()})
        </span>
      ) : null}
    </div>
  );
}

// RawStatusCell: M5.7+ raw-payload TTL affordance. Shows the
// current raw state of an event in the events table and
// provides a one-click freeze/unfreeze toggle. The freeze is
// the escape hatch for "I'm actively debugging this event, keep
// the unredacted vendor payload around past the TTL."
//
// State matrix:
//   raw_present=true,  raw_frozen=false → "✓" (TTL will drop it)
//   raw_present=true,  raw_frozen=true  → "🔒 frozen" (TTL skips it)
//   raw_present=false, raw_frozen=*     → "—" (already dropped;
//                                       unfreeze has no effect
//                                       because we don't restore
//                                       dropped raws)
//
// The freeze action is audit-logged on the api side
// (admin.ingestor_event_freeze) so a privacy review can see
// who pinned which event and when.
function RawStatusCell({ event }: { event: IngestorEventRow }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (raw_frozen: boolean) => ingestorsApi.setRawFrozen(event.id, raw_frozen),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingestor-instance-events', event.instance_id ?? ''] }),
  });
  if (!event.raw_present) {
    return (
      <span className="text-xs text-ink-dim" title="The unredacted raw was dropped by the TTL job.">
        —
      </span>
    );
  }
  if (event.raw_frozen) {
    return (
      <button
        type="button"
        className="text-xs text-accent-warning underline"
        onClick={() => set.mutate(false)}
        disabled={set.isPending}
        title="Pinned by an admin; the TTL job will skip this event. Click to unfreeze (the raw stays on disk until the TTL catches up)."
      >
        🔒 frozen
      </button>
    );
  }
  return (
    <button
      type="button"
      className="text-xs text-ink-dim hover:text-ink underline"
      onClick={() => set.mutate(true)}
      disabled={set.isPending}
      title="Unredacted raw is on disk. The TTL job will drop it after INGESTOR_RAW_TTL_HOURS. Click to pin (skip the TTL for this event)."
    >
      ✓
    </button>
  );
}
